import {
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core'
import { getSalesVolume, SalesVolumeResponse } from 'lib/api'
import { ApiCallStatus, useApiCall } from 'lib/hooks/useApiCall'
import { getPriceFormat } from 'lib/utils'
import { Fragment } from 'react'
import { useTranslation } from 'react-i18next'

interface SalesVolumeTableProps {
  data?: SalesVolumeResponse | null
  loading?: boolean
}

export const SalesVolumeTable = ({ data, loading }: SalesVolumeTableProps = {}): JSX.Element => {
  const { t } = useTranslation()

  const apiCall = useApiCall<typeof getSalesVolume>(getSalesVolume, [], !!data || !!loading)

  const isLoading = loading || (!data && apiCall.status === ApiCallStatus.Fetching)
  const salesData = data || apiCall.data

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" paddingTop={2}>
        <CircularProgress size={24} />
      </Box>
    )
  }

  if ((!data && apiCall.status === ApiCallStatus.Error) || !salesData) {
    return (
      <div className="mt-3">
        <p className="text-danger">{t('error.generic')}</p>
      </div>
    )
  }

  const { subscriptionStart, subscriptionEnd, currency, salesVolumes } = salesData

  if (!subscriptionStart || !subscriptionEnd) {
    return (
      <div className="mt-3">
        <p className="text-muted">{t('subscription.salesVolumeTable.noData')}</p>
      </div>
    )
  }

  const buildSubRows = (key: string, singles?: number, sealed?: number) =>
    singles !== undefined && sealed !== undefined
      ? [
          { key: `${key}Singles`, label: '↳ Singles', value: singles },
          { key: `${key}Sealed`, label: '↳ Non-singles', value: sealed },
        ]
      : []

  const channels = [
    {
      key: 'cardmarket',
      label: 'Cardmarket',
      value: salesVolumes.cardmarket,
      subRows: buildSubRows(
        'cardmarket',
        salesVolumes.cardmarketSingles,
        salesVolumes.cardmarketSealed
      ),
    },
    {
      key: 'cardtrader',
      label: 'CardTrader',
      value: salesVolumes.cardtrader,
      subRows: buildSubRows(
        'cardtrader',
        salesVolumes.cardtraderSingles,
        salesVolumes.cardtraderSealed
      ),
    },
    {
      key: 'shopify',
      label: 'Shopify',
      value: salesVolumes.shopify,
      subRows: buildSubRows('shopify', salesVolumes.shopifySingles, salesVolumes.shopifySealed),
    },
  ].filter((channel) => channel.value !== undefined)

  return (
    <>
      <Typography variant="subtitle2" color="textSecondary" gutterBottom>
        {t('subscription.salesVolumeTable.title')}
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t('common.channel')}</TableCell>
            <TableCell align="right">{t('subscription.salesVolumeTable.salesVolume')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {channels.map((channel) => (
            <Fragment key={channel.key}>
              <TableRow>
                <TableCell>{channel.label}</TableCell>
                <TableCell align="right">{getPriceFormat(channel.value!, currency)}</TableCell>
              </TableRow>
              {channel.subRows.map((subRow) => (
                <TableRow key={subRow.key}>
                  <TableCell style={{ paddingLeft: 32 }}>{subRow.label}</TableCell>
                  <TableCell align="right">{getPriceFormat(subRow.value!, currency)}</TableCell>
                </TableRow>
              ))}
            </Fragment>
          ))}
          <TableRow>
            <TableCell>
              <strong>{t('subscription.salesVolumeTable.total')}</strong>
            </TableCell>
            <TableCell align="right">
              <strong>{getPriceFormat(salesVolumes.total, currency)}</strong>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </>
  )
}
